/**
 * Who she is shopping for — one word, one spelling, one place.
 *
 * This existed in four: the header tabs said `women`, the API says `woman`,
 * the home page held a `ref('women')` that reached nothing, and search fell
 * back to its own `'woman'` constant. So picking the Men tab changed the
 * header's underline and nothing else — every request still asked for
 * womenswear, and would have kept asking even if the tab had been wired,
 * because `men` is not a word the backend knows.
 *
 * The vocabulary here is the BACKEND's. Not out of deference: there is no
 * separate frontend model of an audience — it is one concept spelled two ways,
 * and a translation layer between two spellings of the same thing is where
 * drift goes to hide. The tab's `value` was always an identifier with its
 * label alongside it, so speaking the contract's word costs nothing on screen.
 */

/** The audiences search actually gates on. */
export const AUDIENCES = ['woman', 'man', 'kids', 'unisex'] as const

export type Audience = (typeof AUDIENCES)[number]

export const DEFAULT_AUDIENCE: Audience = 'woman'

/**
 * Spellings we still accept on the way IN.
 *
 * `?audience=women` links are already out there — shared, bookmarked, indexed —
 * and they must keep working. Reading generously while writing one canonical
 * word is not a mapper between two models; it is the edge doing its job.
 */
const LEGACY_SPELLINGS: Readonly<Record<string, Audience>> = {
  women: 'woman',
  womens: 'woman',
  female: 'woman',
  men: 'man',
  mens: 'man',
  male: 'man',
  kid: 'kids',
  children: 'kids',
  child: 'kids',
}

const CANONICAL = new Set<string>(AUDIENCES)

/** The canonical audience for any spelling, or `null` if it names none. */
export function parseAudience(raw: unknown): Audience | null {
  if (typeof raw !== 'string') return null
  const value = raw.trim().toLowerCase()
  if (!value || value === 'undefined' || value === 'null') return null
  if (CANONICAL.has(value)) return value as Audience
  return LEGACY_SPELLINGS[value] ?? null
}

/** Same, but never null — for the many callers that just need an audience. */
export function resolveAudience(raw: unknown): Audience {
  return parseAudience(raw) ?? DEFAULT_AUDIENCE
}

/**
 * Header tabs that are NOT audiences.
 *
 * `Home` and `Beauty` are departments — a room and a category, not a person to
 * dress. Search has no such audience, so sending one would filter on a word the
 * backend does not know and quietly return whatever it returns. They are named
 * here so the tab strip can keep offering them while the audience state
 * refuses to be one of them.
 */
export const DEPARTMENT_TABS: ReadonlySet<string> = new Set(['home', 'beauty'])

export function isAudienceTab(tabValue: string): boolean {
  return !DEPARTMENT_TABS.has(tabValue.toLowerCase())
}
